<!--
  지출결의서 상세 (조회 전용)
  - 2026-06-11 재설계: 인쇄용 표 레이아웃 → 히어로(금액·상태) + kv 그리드 + 입고내역/비고 카드
  - 인쇄는 전용 화면(/expense/:id/print, ExpensePrint.vue) 사용 — 팝업 복사 방식 제거
  - 모바일: 하단 고정 액션바, 역할·상태 기준 다음 단계 버튼만 primary
-->
<template>
  <div class="expense-detail-view">
    <div style="text-align:right; margin-bottom:4px">
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
    </div>
    <HelpPanel v-model="helpVisible" :content-path="'screens/expense-detail'" />

    <!-- 히어로: 지결번호 + 상태 + 금액 -->
    <el-card shadow="never" class="detail-card">
      <div class="hero">
        <div class="hero-top">
          <span class="doc-no">{{ expense.document_number || '-' }}</span>
          <span class="status-chip" :class="`chip-${expense.status}`">{{ statusLabel(expense.status) }}</span>
        </div>
        <div class="hero-amount">{{ formatCurrency(expense.amount, 'KRW') }}</div>
        <div class="hero-sub">
          {{ expense.partner_company_name || '-' }} · 지급 {{ expense.payment_due_date || '-' }}
          <template v-if="expense.currency !== 'KRW'">
            · {{ formatCurrency(expense.amount_foreign, expense.currency) }} × {{ Number(expense.fx_rate).toLocaleString() }}
          </template>
        </div>
      </div>

      <!-- 기본정보 kv 그리드 -->
      <div class="kv-grid">
        <div class="kv"><span class="k">결제조건</span><span class="v">{{ expense.payment_method_name || '-' }}</span></div>
        <div class="kv"><span class="k">비용구분</span><span class="v">{{ expense.category_name || '-' }}</span></div>
        <div class="kv"><span class="k">발주사</span><span class="v">{{ expense.our_compay_name || '-' }}</span></div>
        <div class="kv"><span class="k">마감월</span><span class="v">{{ expense.close_month }}</span></div>
        <div class="kv kv-wide"><span class="k">입금계좌</span><span class="v">{{ accountInfo }}</span></div>
        <div class="kv"><span class="k">작성자</span><span class="v">{{ expense.created_by_name || '-' }}</span></div>
        <div class="kv"><span class="k">작성일자</span><span class="v">{{ expense.created_at || '-' }}</span></div>
      </div>
    </el-card>

    <!-- 집행 근거 (2026-07-15) — 입고 연결·작업지시 배분을 집행자가
         종이 서류 없이 시스템에서 확인. 근거 없으면 경고 배지. -->
    <el-card shadow="never" class="detail-card">
      <div class="section-header">
        <h5>
          집행 근거
          <template v-if="expense.details?.length">{{ expense.details.length }}건</template>
          <el-tag v-else type="warning" size="small" style="margin-left:8px">
            근거 미연결 — 증빙 서류 확인 필요
          </el-tag>
        </h5>
        <span v-if="expense.details?.length" class="details-sum" :class="{ 'details-sum--mismatch': detailsMismatch }">
          합계 {{ formatCurrency(detailsTotal, 'KRW') }}<template v-if="detailsMismatch"> · 금액과 불일치</template>
        </span>
      </div>
      <el-table v-if="expense.details?.length" :data="expense.details" size="small">
        <el-table-column label="근거" width="92">
          <template #default="{ row }">
            <el-tag v-if="row.receiving" size="small" type="success" effect="plain">입고 연결</el-tag>
            <el-tag v-else-if="row.main_request" size="small" type="warning" effect="plain">지시 배분</el-tag>
            <el-tag v-else size="small" type="info" effect="plain">직접 입력</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="order_number" label="발주번호" min-width="120" show-overflow-tooltip />
        <el-table-column prop="received_date" label="입고일" width="95" />
        <el-table-column prop="style_number" label="스타일번호" min-width="95" />
        <el-table-column prop="material_name" label="품명" min-width="110" show-overflow-tooltip />
        <el-table-column prop="specification" label="규격" min-width="80" show-overflow-tooltip />
        <el-table-column prop="quantity" label="수량" align="right" width="70" />
        <el-table-column label="단가" align="right" min-width="90">
          <template #default="{ row }">{{ Number(row.unit_price || 0).toLocaleString() }}</template>
        </el-table-column>
        <el-table-column label="금액" align="right" min-width="100">
          <template #default="{ row }">{{ Number(row.amount || 0).toLocaleString() }}</template>
        </el-table-column>
        <el-table-column prop="cost_category_name" label="원가 분류" width="95" />
      </el-table>
      <div v-else class="no-evidence-hint">
        이 지출결의서에는 연결된 입고내역·작업지시 배분이 없습니다.
        작성 화면의 [입고내역 추가](자재) 또는 [작업지시 배분 추가](임가공임 등)로
        근거를 연결하면 집행 검토와 스타일 원가 집계가 자동화됩니다.
      </div>
    </el-card>

    <!-- 지급 이력 (§13 — 분할/합산 지급 추적, 권한 있는 사용자에게만 표시) -->
    <el-card v-if="payments.length" shadow="never" class="detail-card">
      <div class="section-header">
        <h5>지급 이력 {{ payments.length }}건</h5>
        <span class="details-sum">
          기지급 {{ Number(paidTotal).toLocaleString() }}
          <template v-if="paidRemaining > 0"> · 잔액 {{ Number(paidRemaining).toLocaleString() }}</template>
          <template v-else> · 완납</template>
        </span>
      </div>
      <el-table :data="payments" size="small">
        <el-table-column prop="payment_number" label="지급번호" width="150" />
        <el-table-column label="상태" width="90">
          <template #default="{ row }">
            <el-tag size="small" :type="payStatusTag(row.status)" effect="plain">{{ row.status_display }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="planned_date" label="예정일" width="100" />
        <el-table-column prop="executed_date" label="집행일" width="100">
          <template #default="{ row }">{{ row.executed_date || '-' }}</template>
        </el-table-column>
        <el-table-column prop="executed_by_name" label="집행자" width="90">
          <template #default="{ row }">{{ row.executed_by_name || '-' }}</template>
        </el-table-column>
        <el-table-column prop="bank_account_name" label="출금 계좌" min-width="110" />
        <el-table-column label="이 결의서 배분액" align="right" min-width="120">
          <template #default="{ row }">{{ Number(allocOf(row)).toLocaleString() }}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 증빙 첨부 (2026-07-24 요청) — 접수자·경리부가 첨부 서류를 확인 -->
    <el-card shadow="never" class="detail-card">
      <h5>증빙 첨부
        <span v-if="(expense.attachments || []).length" class="details-sum">{{ expense.attachments.length }}건</span>
      </h5>
      <el-table v-if="(expense.attachments || []).length" :data="expense.attachments" size="small">
        <el-table-column prop="doc_type_display" label="유형" width="110" />
        <el-table-column label="파일" min-width="200">
          <template #default="{ row }">
            <a :href="row.file_url || row.file" target="_blank" rel="noopener">{{ row.original_name || '파일' }}</a>
          </template>
        </el-table-column>
        <el-table-column prop="memo" label="비고" min-width="120" />
        <el-table-column prop="created_at" label="첨부일" width="160" />
      </el-table>
      <p v-else class="empty-hint" style="color:#909399; margin: 4px 0;">첨부된 증빙이 없습니다.</p>
    </el-card>

    <!-- 비고 -->
    <el-card v-if="expense.description" shadow="never" class="detail-card">
      <h5>비고</h5>
      <p class="description">{{ expense.description }}</p>
    </el-card>

    <!-- 액션 바 (모바일: 하단 고정) -->
    <div class="detail-action-bar">
      <el-dropdown trigger="click" @command="onActionCommand">
        <el-button>
          <el-icon><MoreFilled /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="print">프린트</el-dropdown-item>
            <!-- 관리자 정정 (2026-08-13) — 어드민 직접 수정 대체. 정정관리자 그룹만.
                 tasks.LeaveViewSet.correct와 동일 패턴(사유 필수, 감사이력 자동 기록). -->
            <el-dropdown-item v-if="canCorrect" command="correct" divided>정정</el-dropdown-item>
            <el-dropdown-item command="back">뒤로가기</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <el-button v-if="canReject" @click="changeStatus('reject')">반송</el-button>
      <el-button v-if="canEdit" :type="primaryAction ? 'default' : 'primary'" :class="{ 'action-primary': !primaryAction }" @click="editExpense">수정</el-button>
      <el-button
        v-if="primaryAction"
        type="primary"
        class="action-primary"
        @click="changeStatus(primaryAction.status)"
      >{{ primaryAction.label }}</el-button>
      <el-button v-if="!primaryAction && !canEdit" @click="goBack">뒤로가기</el-button>
    </div>

    <!-- 관리자 정정 다이얼로그 (2026-08-13) — Django admin 대신 여기서 처리.
         금액·계좌·마감월·비용구분·결제조건 등 헤더 필드만 대상(입고내역/지급이력은
         기존 화면에서 관리). 거래처(partner_company)·발주처(out_compay) 변경은
         이 다이얼로그 범위 밖 — 필요하면 후속으로 추가. -->
    <el-dialog v-model="correction.visible" title="지출결의서 정정" width="560px">
      <div v-if="correction.visible">
        <div class="corr-doc-info">
          {{ expense.document_number }} · {{ expense.partner_company_name || '-' }} ({{ statusLabel(expense.status) }})
        </div>
        <el-alert type="warning" :closable="false" show-icon class="mb-3"
          title="제출·집행 완료 건도 수정됩니다. 정정 내용과 사유는 감사이력에 자동 기록됩니다." />

        <el-form label-width="90px" size="small">
          <div class="corr-row">
            <el-form-item label="원화 금액"><el-input-number v-model="correction.form.amount" :min="0" :precision="2" :controls="false" style="width:100%" /></el-form-item>
            <el-form-item label="통화">
              <el-select v-model="correction.form.currency" style="width:100%">
                <el-option label="원화" value="KRW" /><el-option label="미국 달러" value="USD" /><el-option label="유로" value="EUR" />
              </el-select>
            </el-form-item>
          </div>
          <div class="corr-row" v-if="correction.form.currency !== 'KRW'">
            <el-form-item label="외화 금액"><el-input-number v-model="correction.form.amount_foreign" :min="0" :precision="2" :controls="false" style="width:100%" /></el-form-item>
            <el-form-item label="적용환율"><el-input-number v-model="correction.form.fx_rate" :min="0" :precision="2" :controls="false" style="width:100%" /></el-form-item>
          </div>
          <div class="corr-row">
            <el-form-item label="부가세구분">
              <el-select v-model="correction.form.vat_type" style="width:100%">
                <el-option label="과세" value="01" /><el-option label="면세" value="02" />
                <el-option label="영세" value="03" /><el-option label="원천징수(3.3%)" value="04" />
              </el-select>
            </el-form-item>
            <el-form-item label="마감월">
              <!-- 2026-08-18: 자유 입력 → 월 선택기. 옛 표기("8")로 저장된 문서는
                   서버가 작성일 연도로 YYYY-MM 보정해 받는다. -->
              <el-date-picker v-model="correction.form.close_month" type="month"
                value-format="YYYY-MM" format="YYYY-MM" placeholder="마감월 선택"
                style="width:100%" />
            </el-form-item>
          </div>
          <el-form-item label="지급요청일">
            <el-date-picker v-model="correction.form.payment_due_date" type="date" value-format="YYYY-MM-DD" style="width:100%" />
          </el-form-item>
          <ExpenseCategorySelect v-model="correction.form.category" />
          <PaymentMethodSelect v-model="correction.form.payment_method" />
          <div class="corr-row">
            <el-form-item label="은행"><el-input v-model="correction.form.bank" /></el-form-item>
            <el-form-item label="SWIFT"><el-input v-model="correction.form.swift_code" /></el-form-item>
          </div>
          <div class="corr-row">
            <el-form-item label="계좌번호"><el-input v-model="correction.form.account_number" /></el-form-item>
            <el-form-item label="예금주"><el-input v-model="correction.form.account_holder" /></el-form-item>
          </div>
          <el-form-item label="비고">
            <el-input v-model="correction.form.description" type="textarea" :rows="2" />
          </el-form-item>
        </el-form>

        <el-input v-model="correction.reason" type="textarea" :rows="2"
          placeholder="정정 사유 (필수) — 예: 세금계산서 확인 결과 금액 오기입" />
      </div>
      <template #footer>
        <el-button @click="correction.visible = false">닫기</el-button>
        <el-button type="warning" :loading="correction.saving" @click="submitCorrection">정정 저장</el-button>
      </template>
    </el-dialog>

    <!-- ─────────────────────────────────────────────────────────
         인쇄 전용 마크업 (화면 비표시) — 기존 인쇄물과 100% 동일 유지 목적.
         '프린트' 버튼이 이 영역의 innerHTML 을 팝업으로 복사해 인쇄한다.
         ⚠ 인쇄물 모양이 바뀌면 안 되므로 이 블록은 수정하지 말 것 (2026-06-11)
         ───────────────────────────────────────────────────────── -->
    <div id="expense-content" class="container mt-4" style="display: none;">
      <div class="row d-flex justify-content-between align-items-center">
        <div class="card border-0" style="flex: 1; max-width: 48%;">
          <div class="card-header bg-light text-center">
            <h4 class="fw-bold ">지출결의서</h4>
          </div>
        </div>
        <div class="card border-0 " style="flex: 1; max-width: 48%;">
          <div class="card-header bg-light  text-center">
            <img :src="imageUrl" alt="결제이미지"  style="max-width: 100%; height: auto;">
          </div>
        </div>
      </div>

      <div class="row">
        <div class="card  ">
          <div class="table-responsive">
            <table class="table table-bordered ">
              <tbody>
                <tr>
                  <th class="bg-light text-center align-middle">지결번호</th>
                  <td class="align-middle">{{ expense.document_number || '-' }}</td>
                  <th class="bg-light text-center align-middle">금액</th>
                  <td class="align-middle fw-bold">{{ formatCurrency(expense.amount, "KRW") || '-' }}  </td>
                </tr>
                <tr>
                  <th class="bg-light text-center align-middle">작성자</th>
                  <td class="align-middle">{{ expense.created_by_name || '-' }}</td>
                  <th class="bg-light text-center align-middle">마감월</th>
                  <td class="align-middle  fw-bold">{{ closeMonthPrint }}</td>
                </tr>
                <tr>
                  <th class="bg-light text-center align-middle">작성일자</th>
                  <td class="align-middle">{{ expense.created_at || '-' }}</td>
                  <th class="bg-light text-center align-middle">적용환율</th>
                  <td class="align-middle">
                    <template v-if="expense.currency !== 'KRW'">
                      {{ formatCurrency(expense.amount_foreign, expense.currency) || '-' }}
                      *
                      {{ formatCurrency(expense.fx_rate, "KRW") || '-' }}
                    </template>
                    <template v-else>
                      -
                    </template>
                  </td>
                </tr>
                <tr>
                  <th class="bg-light text-center align-middle">지급요청일</th>
                  <td class="align-middle fw-bold">{{ expense.payment_due_date || '-' }}</td>
                  <th class="bg-light text-center align-middle">거래처</th>
                  <td class="align-middle  fw-bold">{{ expense.partner_company_name || '-' }}</td>
                </tr>
                <tr>
                  <th class="bg-light text-center align-middle">결제조건</th>
                  <td class="align-middle">{{ expense.payment_method_name || '-' }}</td>
                  <th class="bg-light text-center align-middle">입금계좌정보</th>
                  <td class="align-middle">{{ expense.bank }} /{{ expense.account_number || '-' }}/{{ expense.account_holder }}</td>
                </tr>
                <tr>
                  <th class="bg-light text-center align-middle">우리회사</th>
                  <td class="align-middle">{{ expense.our_compay_name || '-' }}</td>
                  <th class="bg-light text-center align-middle">비용구분</th>
                  <td class="align-middle">{{ expense.category_name || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="card  flex-fill">
          <div class="card-header fw-bold bg-light text-left">비고</div>
          <div class="card-body">
            <p style="white-space: pre-wrap;" class="card-text text-secondary">{{ expense.description || '-' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
const helpVisible = ref(false)
import { ref, computed, onMounted, onActivated } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axiosInstance from '@/api/axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import { MoreFilled } from '@element-plus/icons-vue';
import { formatCurrency } from "@/utils/currency";
import { formatCloseMonthShort } from "@/utils/closeMonth";
import { useUserStore } from "@/stores/user";
import { useTabsStore } from "@/stores/tabsStore";
import 결제이미지 from '@/assets/결제이미지.png';
import ExpenseCategorySelect from '@/components/ExpenseCategorySelect.vue';
import PaymentMethodSelect from '@/components/PaymentMethodSelect.vue';

const imageUrl = 결제이미지;

const expense = ref({
  category: '',
  document_number: '',
  amount: 0,
  amount_foreign: 0,
  close_month: '',
  fx_rate: 0,
  currency: 'KRW',
  payment_due_date: '',
  partner_company_name: '',
  payment_method_name: '',
  account_number: '',
  account_holder: '',
  bank: '',
  category_name: '',
  our_compay_name: '',
  description: '',
  status: 'draft',
  created_by_name: '',
  created_at: '',
  details: [],
  is_creator: false,
});

const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const tabsStore = useTabsStore();

const status = computed(() => expense.value.status);
// 인쇄용 마감월 표기 (2026-08-28) — 저장값은 'YYYY-MM'이지만
// 지출결의서 출력물에는 월만('08월') 표기한다.
const closeMonthPrint = computed(() => formatCloseMonthShort(expense.value.close_month));

// 버튼 활성화 조건 (역할 × 상태)
const canEdit = computed(() => expense.value.is_creator && ['draft'].includes(status.value));
const canReceive = computed(() => status.value === "draft" && userStore.isExpenseReceiver);
const canProcess = computed(() => status.value === "received" && userStore.isExpenseExecutor);
const canComplete = computed(() => status.value === "processing" && userStore.isExpenseExecutor);
const canReject = computed(() => (status.value === "received" || status.value === "processing") && userStore.isExpenseReceiver);

// 관리자 정정 (2026-08-13) — 정정관리자 그룹(슈퍼유저 포함)만, 작성중 문서는 대상 아님
// (작성중은 작성자가 '수정' 버튼으로 직접 고침) — tasks.LeaveViewSet과 동일한 게이팅.
const canCorrect = computed(() => userStore.hasGroup('정정관리자') && status.value !== 'draft');

// 현재 역할·상태에서 의미 있는 "다음 단계" 1개만 primary 로 노출
const primaryAction = computed(() => {
  if (canReceive.value) return { status: 'received', label: '경리과 접수' };
  if (canProcess.value) return { status: 'processing', label: '집행 중으로 변경' };
  if (canComplete.value) return { status: 'completed', label: '집행 완료' };
  return null;
});

// 상태 라벨
const statusLabelMap = {
  draft: '작성 중',
  submitted: '제출 완료',
  received: '경리과 접수',
  processing: '집행 중',
  completed: '집행 완료',
  reject: '반송',
};
const statusLabel = (s) => statusLabelMap[s] || s || '-';

// 입금계좌 한 줄 요약
const accountInfo = computed(() => {
  const parts = [expense.value.bank, expense.value.account_number, expense.value.account_holder].filter(Boolean);
  return parts.length ? parts.join(' / ') : '-';
});

// 입고내역 합계 — 헤더 금액과 불일치 시 경고 (수정 화면과 동일 패턴)
const detailsTotal = computed(() =>
  (expense.value.details || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
);
const detailsMismatch = computed(() =>
  (expense.value.details || []).length > 0 &&
  Math.round(detailsTotal.value) !== Math.round(Number(expense.value.amount) || 0)
);

// 데이터 로드
const loadExpense = async (id) => {
  try {
    const response = await axiosInstance.get(`expenses/${id}/`);
    expense.value = response.data;
    // 탭 제목에 문서번호 부제 표시 — 동일 화면 다중 탭 구분 (2026-08-01)
    if (expense.value.document_number) tabsStore.setSubtitle(route.fullPath, expense.value.document_number);
  } catch (error) {
    console.error('Error loading expense:', error);
  }
  loadPayments(id);
};

// ── 지급 이력 (§13) — 권한 없는 사용자는 403 → 카드 숨김 ──
const payments = ref([]);
const loadPayments = async (id) => {
  try {
    const res = await axiosInstance.get('/finance/payments/', { params: { expense: id } });
    const rows = res.data?.results || res.data || [];
    payments.value = rows.filter((p) => p.status !== 'CANCELLED');
  } catch {
    payments.value = []; // 조회 권한 없음 등 — 조용히 숨김
  }
};
const payStatusTag = (s) => ({ EXECUTED: 'success', REQUESTED: 'warning', PLANNED: 'primary' }[s] || 'info');
const allocOf = (p) =>
  (p.allocations || []).find((a) => String(a.expense) === String(expense.value.id))?.allocated_amount || 0;
const paidTotal = computed(() =>
  payments.value
    .filter((p) => p.status === 'EXECUTED')
    .reduce((s, p) => s + Number(allocOf(p)), 0)
);
const paidRemaining = computed(() => Math.max(0, Number(expense.value.amount || 0) - paidTotal.value));

const editExpense = () => {
  router.push(`/expenses/${route.params.id}/edit`);
};

const warnings = {
  reject: "반송 처리하시겠습니까?",
  received: "경리과 접수 상태로 변경됩니다. 계속하시겠습니까?",
  processing: "집행 중 상태로 변경됩니다. 계속하시겠습니까?",
  completed: "집행 완료 후에는 변경할 수 없습니다. 계속하시겠습니까?",
};

const changeStatus = async (newStatus) => {
  try {
    await ElMessageBox.confirm(warnings[newStatus], '상태 변경', {
      confirmButtonText: '확인',
      cancelButtonText: '취소',
      type: 'warning',
    });
  } catch {
    return; // 사용자가 취소
  }

  try {
    await axiosInstance.post(`expenses/${expense.value.id}/change-status/`, {
      new_status: newStatus,
    });
    ElMessage.success(`'${statusLabel(newStatus)}' 처리되었습니다.`);
    await loadExpense(expense.value.id);
  } catch (error) {
    ElMessage.error("상태 변경 중 오류가 발생했습니다.");
  }
};

const goBack = () => {
  history.back();
};

// 인쇄: 기존 방식 그대로 — 숨김 보존된 #expense-content 를 팝업으로 복사해 인쇄
// (인쇄물 모양은 변경 전과 100% 동일하게 유지)
const openPrintPopup = () => {
  const content = document.getElementById("expense-content").innerHTML;

  const popupWindow = window.open(
    "",
    "Print",
    "width=800,height=600,top=100,left=100"
  );

  popupWindow.document.write(`
    <html>
      <head>
        <title>지출결의서</title>
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css">
        <style>
          @media print {
            @page{
              margin:0;
            }

            body { margin: 0; padding: 0; width: 210mm; height: 297mm; }
            #print-content {
              width: 210mm;
              height: 297mm;
              margin: 0;
              padding: 0;
              page-break-inside: avoid;
            }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }

          h3 {
            margin-bottom: 20px;
          }
        </style>
      </head>
      <body>
        ${content}
        <script>
          window.onload = function() {
            window.print();
            window.close();
          };
        <\\/script>
      </body>
    </html>
  `);

  popupWindow.document.close();
};

const onActionCommand = (cmd) => {
  if (cmd === 'print') openPrintPopup();
  else if (cmd === 'back') goBack();
  else if (cmd === 'correct') openCorrection();
};

// ── 관리자 정정 (2026-08-13) — LeaveAdminRequestsView.vue의 정정 다이얼로그와 동일 패턴 ──
const correction = ref({
  visible: false, saving: false, reason: '',
  form: {
    amount: 0, amount_foreign: 0, fx_rate: 0, currency: 'KRW', vat_type: '01',
    close_month: '', payment_due_date: '', bank: '', swift_code: '',
    account_number: '', account_holder: '', description: '',
    category: null, payment_method: null,
  },
});

const openCorrection = () => {
  const e = expense.value;
  correction.value = {
    visible: true, saving: false, reason: '',
    form: {
      amount: Number(e.amount) || 0,
      amount_foreign: Number(e.amount_foreign) || 0,
      fx_rate: Number(e.fx_rate) || 0,
      currency: e.currency || 'KRW',
      vat_type: e.vat_type || '01',
      close_month: e.close_month || '',
      payment_due_date: e.payment_due_date || '',
      bank: e.bank || '',
      swift_code: e.swift_code || '',
      account_number: e.account_number || '',
      account_holder: e.account_holder || '',
      description: e.description || '',
      category: e.category,
      payment_method: e.payment_method,
    },
  };
};

const extractApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  const firstKey = Object.keys(data)[0];
  if (!firstKey) return fallback;
  const value = data[firstKey];
  return Array.isArray(value) ? value[0] : value || fallback;
};

const submitCorrection = async () => {
  const c = correction.value;
  if (!c.reason.trim()) {
    ElMessage.warning('정정 사유를 입력하세요.');
    return;
  }
  c.saving = true;
  try {
    await axiosInstance.post(`expenses/${expense.value.id}/correct/`, {
      reason: c.reason, ...c.form,
    });
    ElMessage.success('정정되었습니다.');
    c.visible = false;
    await loadExpense(expense.value.id);
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '정정에 실패했습니다.'));
  } finally {
    c.saving = false;
  }
};

// keep-alive 탭 복귀 시 재조회 (2026-11-03) — 수정 화면/목록 일괄 처리 뒤 돌아오면 옛 상태가 보이던 문제
let activatedOnce = false; // 첫 activated 는 onMounted 직후라 건너뜀
onActivated(() => {
  if (activatedOnce && expense.value?.id) loadExpense(expense.value.id);
  activatedOnce = true;
});
onMounted(() => {
  loadExpense(route.params.id);
});
</script>

<style scoped>
.corr-doc-info { font-size: 13px; color: #606266; margin-bottom: 10px; }
.corr-row { display: flex; gap: 12px; }
.corr-row > .el-form-item { flex: 1; min-width: 0; }
.mb-3 { margin-bottom: 12px; }
.no-evidence-hint {
  font-size: 12.5px;
  color: #909399;
  line-height: 1.7;
  padding: 4px 2px;
}
.expense-detail-view {
  max-width: 860px;
  margin: 0 auto;
}

.detail-card {
  margin-bottom: 12px;
}
.detail-card :deep(.el-card__body) {
  padding: 14px 16px;
}
.detail-card h5 {
  margin: 0 0 8px;
  font-size: 13px;
  color: #909399;
}

/* ===== 히어로 ===== */
.hero-top {
  display: flex;
  align-items: center;
  gap: 8px;
}
.hero-top .doc-no {
  font-size: 13px;
  color: #909399;
  font-variant-numeric: tabular-nums;
}
.hero-amount {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}
.hero-sub {
  font-size: 13px;
  color: #606266;
  margin-top: 2px;
}

/* ===== 상태칩 (리스트 화면과 동일 팔레트) ===== */
.status-chip {
  padding: 1px 8px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.7;
}
.chip-draft      { background: #f4f4f5; color: #909399; }
.chip-submitted  { background: #ecf5ff; color: #409eff; }
.chip-received   { background: #fdf6ec; color: #e6a23c; }
.chip-processing { background: #f0f9eb; color: #67c23a; }
.chip-completed  { background: #303133; color: #fff; }

/* ===== kv 그리드 ===== */
.kv-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 24px;
  border-top: 1px solid #ebeef5;
  margin-top: 10px;
  padding-top: 8px;
}
.kv {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 3px 0;
  font-size: 13px;
  min-width: 0;
}
.kv .k {
  flex-shrink: 0;
  width: 64px;
  color: #909399;
}
.kv .v {
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kv-wide { grid-column: 1 / -1; }

/* ===== 섹션 헤더 + 합계 ===== */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.section-header h5 { margin: 0; }
.details-sum {
  font-size: 12px;
  font-weight: 600;
  color: #67c23a;
  font-variant-numeric: tabular-nums;
}
.details-sum--mismatch { color: #e6a23c; }

.description {
  margin: 0;
  font-size: 13px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ===== 액션 바 ===== */
.detail-action-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 4px 0 10px;
}
.detail-action-bar .el-button { margin-left: 0; }

@media (max-width: 768px) {
  .expense-detail-view {
    /* 하단 고정 액션바에 가리지 않도록 */
    padding-bottom: 64px;
  }
  .kv-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .detail-action-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    background: #fff;
    border-top: 1px solid #ebeef5;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
    padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
  }
  .detail-action-bar .action-primary { flex: 1; }
}
</style>
